import {
  publishOpenCopilotChat,
  subscribeAddCopilotChatReference,
  subscribeOpenCopilotChat,
} from '@github-ui/copilot-chat-events'
import type {OpenCopilotChatEvent} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CopilotIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useRef} from 'react'

import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

// Diff-reference open events carry a non-empty `references` array. When quick
// chat owns diff events these must open quick chat instead of the docked panel.
function openEventHasReferences(event: OpenCopilotChatEvent): boolean {
  const {payload} = event
  return 'references' in payload && Array.isArray(payload.references) && payload.references.length > 0
}

export function OpenCopilotChatPanelButton({
  size = 'small',
  variant = 'invisible',
  className,
}: {
  size?: 'small' | 'medium'
  variant?: 'invisible' | 'default'
  className?: string
}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const actions = useDockedPanelActions()
  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  // Quick chat only owns diff events when it is actually mounted (the quick
  // chat flag) AND has been given ownership of diff events. Gating on both
  // avoids suppressing the docked panel when quick chat isn't present to
  // receive the event, which would otherwise leave diff-reference clicks with
  // no chat surface on the PR files view.
  const quickChatOwnsDiffEvents =
    isFeatureEnabled('copilot_chat_app_embedded') && isFeatureEnabled('copilot_chat_app_embedded_owns_diff_events')

  // Intercept open-copilot-chat events so chat opens in the side panel
  // instead of the immersive embedded overlay on the PR files view. When quick
  // chat owns diff events, defer diff-reference open events to quick chat so the
  // docked panel doesn't also open (both surfaces would otherwise appear).
  useEffect(() => {
    return subscribeOpenCopilotChat(event => {
      if (quickChatOwnsDiffEvents && openEventHasReferences(event)) return
      if (!isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen, quickChatOwnsDiffEvents])

  // Intercept add-copilot-chat-reference events that request panel opening
  // so the reference is attached in the docked side panel instead of the
  // embedded overlay. When quick chat owns diff events it handles these
  // references itself, so the docked panel stays closed.
  useEffect(() => {
    return subscribeAddCopilotChatReference(e => {
      if (quickChatOwnsDiffEvents) return
      if (e.openPanel && !isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen, quickChatOwnsDiffEvents])

  const buttonAriaLabel = isDockedPanelOpen ? 'Close Copilot panel' : 'Open Copilot panel'

  return (
    <div className={`d-flex flex-items-center${className ? ` ${className}` : ''}`}>
      <ErrorBoundary
        boundaryName="OpenCopilotChatPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The Copilot panel cannot currently be opened."
            icon={CopilotIcon}
            size={size}
            variant={variant}
          />
        }
      >
        <IconButton
          ref={togglePanelRef}
          aria-label={buttonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={CopilotIcon}
          size={size}
          variant={variant}
          onClick={() => {
            sendPullRequestAnalyticsEvent('copilot_chat_side_panel.open', 'COPILOT_CHAT_SIDE_PANEL_BUTTON')
            if (isDockedPanelOpen) {
              actions.closePanel()
            } else {
              // Fire the standard open event so handleOpenPanelEvent sets up
              // chat state (chatIsOpen, thread selection) before the panel DOM
              // mounts. The subscribeOpenCopilotChat handler above will open
              // the panel. This avoids a flash of stale thread content.
              publishOpenCopilotChat({intent: CopilotChatIntents.conversation})
            }
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
